import React, { useMemo } from "react";
import Highcharts from "../../config/highcharts-config";
import HighchartsReact from "highcharts-react-official";
import { BarChartConfig } from "@/store/slices/chart-config-slice";
import { QuestionResponsesSearchResponse } from "@/models/Filter";
import { getChartColor } from "@/utils/chart-utils";
import {
  processGroupedBarChartData,
  resolveGroupedBarRender,
  remapLiveGroupedData,
  resolveGroupedSeriesColors,
  type GroupedBarTransformResult,
} from "@/utils/grouped-bar-utils";
import type { GroupedBarDimension } from "@store/slices/chart-config-slice";
import EmptyChartState from "@components/common/EmptyChartState";
import GroupedBarFooter from "./GroupedBarFooter";
import { useGroupedBarFooter } from "@hooks/use-grouped-bar-footer";

/** Significance arrow markup, matching the Bar Chart convention. */
function getSignificanceArrow(sig?: number): string {
  if (sig === 1) return ' <span style="color: #10B981;">▲</span>';
  if (sig === -1) return ' <span style="color: #EF4444;">▼</span>';
  return "";
}

interface GroupedBarChartProps {
  config?: BarChartConfig;
  filteredData?: QuestionResponsesSearchResponse | null;
  weightsApplied?: boolean;
  hasCustomTitle?: boolean | null;
  dashboardDateOverride?: { startDate: string | null; endDate: string | null };
  viewerState?: {
    rollingAverage: number | null;
    timeUnit: string | null;
    timePeriod: string | null;
  };
  dataAvailabilityDate?: string | null;
}

/**
 * Grouped bar (grouped column) chart. Renders multiple series side-by-side
 * within each category using Highcharts' default grouping. Built from grid
 * questions where every item carries a full descriptiveMetrics[] array.
 */
const GroupedBarChart: React.FC<GroupedBarChartProps> = ({
  config,
  filteredData,
  weightsApplied = true,
  hasCustomTitle,
  dashboardDateOverride,
  viewerState,
  dataAvailabilityDate,
}) => {
  const { categories, series } = useMemo(() => {
    // Prefer freshly fetched data (respects active dashboard filters); fall back
    // to the id-carrying structure persisted in the saved grouped config.
    // Live data uses response ids; remap them to the reconciled ids/labels via
    // the stored chartData so selection/overrides still apply on the dashboard.
    const base: GroupedBarTransformResult =
      filteredData?.data && Array.isArray(filteredData.data)
        ? remapLiveGroupedData(
            processGroupedBarChartData(filteredData.data as any[]),
            config?.groupedBar?.chartData,
          )
        : (config?.groupedBar?.chartData ?? { categories: [], series: [] });

    // Group By (inverted focusMode): VARIABLE puts values on the x-axis.
    const groupBy: GroupedBarDimension =
      config?.focusMode?.toLowerCase() === "variable" ? "value" : "variable";

    return resolveGroupedBarRender(base, config?.groupedBar, groupBy);
  }, [filteredData, config?.groupedBar, config?.focusMode]);

  const seriesColorById = useMemo(() => {
    const groupBy: GroupedBarDimension =
      config?.focusMode?.toLowerCase() === "variable" ? "value" : "variable";
    return resolveGroupedSeriesColors(config?.groupedBar, groupBy);
  }, [config?.groupedBar, config?.focusMode]);

  const options = useMemo<Highcharts.Options>(() => {
    const chartType: "bar" | "column" =
      config?.orientation === "horizontal" ? "bar" : "column";
    const decimals = config?.percentageFormatting ?? 0;
    // Grouped bar keeps text at full size in compact mode — compact only tightens
    // bar spacing. Shrinking the group names, legend series names and per-bar
    // percentages made a multi-bar-per-group chart look cramped.
    const fontSize = "12px";
    const showValues = config?.valuePlacement && config.valuePlacement !== "none";
    // Significance markers ride on the value label, matching the Bar Chart. The
    // per-cell statSignificance comes from the response (only non-zero when stat
    // testing is enabled), carried through as point.custom.
    const statTestingEnabled = config?.statisticalTesting?.enabled ?? false;

    const dataLabels = {
      enabled: !!showValues,
      inside: config?.valuePlacement === "inside",
      // HTML labels (for the colored significance arrow), matching Bar Chart. The
      // tooltip's `outside: true` keeps it layered above these on hover.
      useHTML: true,
      formatter: function (this: any) {
        const base = `${this.y?.toFixed(decimals)}%`;
        return statTestingEnabled
          ? base + getSignificanceArrow(this.point?.custom?.statSignificance)
          : base;
      },
      style: { fontSize, fontWeight: "normal" },
    } as Highcharts.PlotColumnOptions["dataLabels"];

    return {
      chart: {
        type: chartType,
        backgroundColor: "transparent",
        height: "auto",
      },
      title: { text: undefined },
      xAxis: {
        categories,
        crosshair: true,
        labels: { style: { fontSize } },
      },
      yAxis: {
        min: 0,
        visible: config?.showValueAxis ?? true,
        title: { text: "Percentage (%)" },
        labels: { format: `{value:.${decimals}f}%`, style: { fontSize } },
      },
      tooltip: {
        shared: true,
        // Render outside the SVG (above HTML data labels) so significance-marker
        // labels don't bleed through the tooltip on hover, matching Bar Chart.
        outside: true,
        style: { zIndex: 9999 },
        valueSuffix: "%",
        valueDecimals: decimals,
      },
      plotOptions: {
        column: {
          pointPadding: config?.compactMode ? 0.02 : 0.1,
          groupPadding: config?.compactMode ? 0.05 : 0.2,
          borderWidth: 0,
          dataLabels,
        },
        bar: {
          pointPadding: config?.compactMode ? 0.02 : 0.1,
          groupPadding: config?.compactMode ? 0.05 : 0.2,
          borderWidth: 0,
          dataLabels,
        },
      },
      legend: {
        enabled: true,
        itemStyle: { fontSize },
      },
      credits: { enabled: false },
      exporting: { enabled: false },
      series: series.map((s, index) => ({
        type: chartType,
        name: s.label,
        data: s.data.map((cell) => ({
          y: cell.value,
          custom: { statSignificance: cell.statSignificance },
        })),
        color: seriesColorById.get(s.id) ?? getChartColor(index),
      })) as Highcharts.SeriesOptionsType[],
    };
  }, [categories, series, config, seriesColorById]);

  const footerData = useGroupedBarFooter(config, filteredData, {
    weightsApplied,
    hasCustomTitle,
    dashboardDateOverride,
    viewerState,
    dataAvailabilityDate,
  });

  if (categories.length === 0 || series.length === 0) {
    return <EmptyChartState />;
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex-1 min-h-0">
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          containerProps={{ style: { width: "100%", height: "100%" } }}
        />
      </div>
      <GroupedBarFooter data={footerData} />
    </div>
  );
};

export default GroupedBarChart;
